import { createContext, useContext, useCallback, useState, useMemo } from 'react';
import type { ReactNode } from 'react';

export type CartItem = {
  id: string;
  name: string;
  price: string;
  image: string;
  quantity: number;
  size?: string;
};

type CartContextValue = {
  items: CartItem[];
  totalCount: number;
  totalPrice: string;
  addItem: (item: Omit<CartItem, 'quantity'>, qty?: number, size?: string) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  toast: string | null;
  showToast: (msg: string) => void;
};

const CartContext = createContext<CartContextValue | null>(null);

function parsePrice(price: string): number {
  if (price === '—') return 0;
  const n = parseFloat(price.replace(/[^0-9.]/g, ''));
  return isNaN(n) ? 0 : n;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [toast, setToastState] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToastState(msg);
    const t = setTimeout(() => setToastState(null), 2200);
    return () => clearTimeout(t);
  }, []);

  const addItem = useCallback((item: Omit<CartItem, 'quantity'>, qty = 1, size?: string) => {
    const lineId = size ? `${item.id}-${size}` : item.id;
    const withSize = size ? { ...item, size } : item;
    setItems((prev) => {
      const i = prev.findIndex((x) => x.id === lineId);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], quantity: next[i].quantity + qty };
        return next;
      }
      return [...prev, { ...withSize, id: lineId, quantity: qty }];
    });
    showToast('Added to bag');
  }, [showToast]);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    if (quantity < 1) {
      removeItem(id);
      return;
    }
    setItems((prev) =>
      prev.map((x) => (x.id === id ? { ...x, quantity } : x))
    );
  }, [removeItem]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen((o) => !o), []);

  const totalCount = useMemo(() => items.reduce((a, x) => a + x.quantity, 0), [items]);
  const totalPrice = useMemo(() => {
    const sum = items.reduce((a, x) => a + parsePrice(x.price) * x.quantity, 0);
    return sum ? `$${sum.toFixed(0)}` : '$0';
  }, [items]);

  const value = useMemo(
    () => ({
      items,
      totalCount,
      totalPrice,
      addItem,
      removeItem,
      updateQuantity,
      isOpen,
      openCart,
      closeCart,
      toggleCart,
      toast,
      showToast,
    }),
    [
      items,
      totalCount,
      totalPrice,
      addItem,
      removeItem,
      updateQuantity,
      isOpen,
      openCart,
      closeCart,
      toggleCart,
      toast,
      showToast,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
